import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  AddressInputField,
  Button,
  DropDownInput,
  InputField,
  UploadInput,
} from "@/components";
import { IoArrowForward } from "react-icons/io5";
import { statesOptionsArray } from "../../../utils/Data.js";
import { gstDetailsSchema } from "../../../schemas";
import { useFieldError, useValidation } from "@/";

const GstDetails = () => {
  const {
    handleNextStep,
    handlePreviousStep,
    handleSkipStep,
    formData,
    setFormData,
    isDisabled,
  } = useOutletContext();
  const { validate, errors } = useValidation(gstDetailsSchema);
  const [formErrors, setFormErrors] = useState(errors);
  const getErrorMessage = useFieldError(formErrors || []);

  // Helper functions to update gstDetails in parent's state
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessDetails: {
        ...prev.businessDetails,
        [field]: value,
      },
    }));
  };

  // For nested address fields in businessDetails.address
  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessDetails: {
        ...prev.businessDetails,
        address: {
          ...prev.businessDetails.address,
          [field]: value,
        },
      },
    }));
  };

  // Options for Business Organization Type dropdown
  const BusinessOrganizationTypes = {
    Type1: "Sole Proprietorship",
    Type2: "Partnership Firm",
    Type3: "Limited Liability Partnership (LLP)",
    Type4: "Private Limited Company",
    Type5: "Public Limited Company",
    Type6: "One Person Company (OPC)",
    Type7: "Cooperative Society",
    Type8: "Joint Hindu Family Business",
    Type9: "Section 8 Company",
    Type10: "Trusts",
    Type11: "Non-Banking Financial Companies (NBFCs)",
    Type12: "Franchise Business",
  };

  const businessOrganizationOptions = Object.keys(
    BusinessOrganizationTypes
  ).map((key) => ({
    label: BusinessOrganizationTypes[key],
    value: BusinessOrganizationTypes[key],
  }));

  const skip = () => {
    handleSkipStep();
  };

  const previous = () => {
    handlePreviousStep();
  };

  const handleNext = () => {
    if (isDisabled) {
      handleNextStep();
      return;
    }

    setFormErrors([]);

    const result = validate(formData.businessDetails);

    if (!result.isValid) {
      console.log("Validation errors:", result.errors);
      setFormErrors(result.errors);
      return;
    }

    console.log("GST data:", result.data);
    handleNextStep();
  };

  return (
    <div className="flex flex-col gap-4 md:gap-4 w-full p-2">
      <h2 className="md:text-headline3 text-headline4 font-nunito text-secondary-90 font-bold">
        GST Details
      </h2>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-col gap-3 w-full "
      >
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Firm Name"
            type="text"
            placeholder="Enter firm name"
            value={formData?.businessDetails?.firmName || ""}
            onChange={(val) => handleChange("firmName", val)}
            error={getErrorMessage("firmName")}
            disabled={isDisabled}
          />
          <InputField
            label="Firm Phone No."
            type="number"
            placeholder="Enter firm phone number"
            value={formData?.businessDetails?.phone || ""}
            onChange={(val) => handleChange("phone", val)}
            maxLength={10}
            error={getErrorMessage("phone")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Pincode"
            type="number"
            placeholder="Enter pincode"
            value={formData?.businessDetails?.address?.pincode || ""}
            onChange={(val) => handleAddressChange("pincode", val)}
            error={getErrorMessage("address.pincode")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="State"
            value={formData?.businessDetails?.address?.state || ""}
            onChange={(value) => handleAddressChange("state", value)}
            placeholder="Select your state"
            options={statesOptionsArray}
            error={getErrorMessage("address.state")}
            disabled={isDisabled}
          />
          <InputField
            label="City"
            type="text"
            placeholder="Enter city"
            value={formData?.businessDetails?.address?.city || ""}
            onChange={(val) => handleAddressChange("city", val)}
            error={getErrorMessage("address.city")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <AddressInputField
            label="Address"
            placeholder="Enter address"
            value={formData?.businessDetails?.address?.line1 || ""}
            onChange={(val) => handleAddressChange("line1", val)}
            error={getErrorMessage("address.line1")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="Business Organization Type"
            value={formData?.businessDetails?.businessType || ""}
            onChange={(value) => handleChange("businessType", value)}
            placeholder="Select your business type"
            options={businessOrganizationOptions}
            error={getErrorMessage("businessType")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="GST No."
            type="text"
            placeholder="Enter GST No."
            value={formData?.businessDetails?.gstNumber || ""}
            onChange={(val) => handleChange("gstNumber", val)}
            error={getErrorMessage("gstNumber")}
            disabled={isDisabled}
          />
          <InputField
            label="Firm PAN No."
            type="text"
            placeholder="Enter Firm PAN No."
            value={formData?.businessDetails?.firmPanNumber || ""}
            onChange={(val) => handleChange("firmPanNumber", val)}
            error={getErrorMessage("firmPanNumber")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <UploadInput
            label="GST Registration Certificate"
            placeholder="Upload registration certificate."
            value={formData?.firmGST || null}
            onChange={(val) => handleChange("firmGST", val)}
            note="Note: File type should only be png, jpeg and should be in color"
            noteClassName="text-caption md:text-body1 pr-2"
            spanClassName="text-caption md:text-body1"
            error={getErrorMessage("firmGST")}
            disabled={isDisabled}
          />
          <UploadInput
            label="Firm PAN Card"
            placeholder="Upload Firm PAN Card."
            value={formData?.firmPan || null}
            onChange={(val) => handleChange("firmPan", val)}
            note="Note: File type should only be png, jpeg and should be in color"
            noteClassName="text-caption md:text-body1 pr-2"
            spanClassName="text-caption md:text-body1"
            error={getErrorMessage("firmPan")}
            disabled={isDisabled}
          />
        </div>

        <div className="flex gap-4 w-full md:w-1/2">
          <UploadInput
            label="Firm Logo"
            placeholder="Upload Firm Logo."
            value={formData?.firmLogo || null}
            onChange={(val) => handleChange("firmLOGO", val)}
            note="Note: PNG/JPEG only, colored. Logo ratio 1 ratio 1 or 19 ratio 6"
            noteClassName="text-caption md:text-body1 pr-2"
            spanClassName="text-caption md:text-body1"
            error={getErrorMessage("firmLOGO")}
            disabled={isDisabled}
          />
        </div>
      </form>
      <div className="flex flex-row gap-1 md:gap-2 justify-between sm:justify-end items-end">
        <div className="flex gap-1 md:gap-2 mt-8 mr-2 items-end">
          <Button size="sm" variant="outline" onClick={previous}>
            Previous
          </Button>
          {!isDisabled && (
            <Button
              size="sm"
              variant="outline"
              onClick={skip}
              className="min-w-16"
            >
              Skip
            </Button>
          )}
        </div>
        <Button
          size="sm"
          variant="default"
          onClick={handleNext}
          icon={IoArrowForward}
          className="h-fit"
        >
          {isDisabled ? "Next" : "Save And Next"}
        </Button>
      </div>
    </div>
  );
};

export default GstDetails;
